import React from "react";
import "../styles/what-if-analysis.css";

import { getWhatIfAnalysis } from "../services/whatIfService";
import { getProcessInformation } from "../services/processService";
import { CURRENCY_TYPES } from "../services/currencyService";
import useCurrencyRates from "../hooks/useCurrencyRates";
import SearchableDropdown from "../components/ui/SearchableDropdown";
import FormModal from "../components/ui/FormModal";
import MetricsPanel from "../components/whatif/MetricsPanel";
import RaciMatrix from "../components/whatif/RaciMatrix";
import ScenarioEditor from "../components/whatif/ScenarioEditor";
import { normalizeWhatIfResponse } from "../utils/whatif/whatIfNormalize";
import { TIME_UNITS } from "../utils/whatif/whatIfFormat";

const STATUS = {
  idle: "idle",
  loading: "loading",
  ready: "ready",
  error: "error",
};

// Friendlier headings for error codes the endpoint reports in `detail.error`.
// Anything unlisted falls back to a generic title; the server's message is shown
// either way, so an unmapped code still reads sensibly.
const FAILURE_TITLES = {
  NO_TASKS: "Not enough tasks",
};

// Processes are labelled "Name (V1)| Company" so versions of the same process
// stay distinguishable in the combobox.
const buildProcessOptions = (processes) => {
  const seen = new Set();
  return (Array.isArray(processes) ? processes : []).reduce((options, item) => {
    const name = item.process_name || item.name || "Unnamed process";
    const version = item.process_version ?? item.version ?? 0;
    const company = item.company?.name || "";
    const label = `${name} (V${version})| ${company}`;
    const id = item.process_id || item.id;

    if (id != null && !seen.has(label)) {
      seen.add(label);
      // The company travels with the option so the scenario editor can offer
      // that company's other jobs when reassigning a role.
      options.push({
        label,
        value: String(id),
        companyId: item.company?.company_id ?? item.company_id ?? null,
      });
    }
    return options;
  }, []);
};

const WhatIfAnalysisPage = () => {
  const { rates } = useCurrencyRates();

  const [processOptions, setProcessOptions] = React.useState([]);
  const [selectedLabel, setSelectedLabel] = React.useState("");
  const [selectedProcessId, setSelectedProcessId] = React.useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = React.useState(null);

  const [status, setStatus] = React.useState(STATUS.idle);
  const [analysis, setAnalysis] = React.useState(null);
  const [failure, setFailure] = React.useState(null);

  const [timeUnit, setTimeUnit] = React.useState("hours");
  const [currency, setCurrency] = React.useState("USD");

  const [scenarioOpen, setScenarioOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const processes = await getProcessInformation();
        if (!cancelled) setProcessOptions(buildProcessOptions(processes));
      } catch (error) {
        console.error("Failed to load processes for What-if Analysis:", error);
        if (!cancelled) setProcessOptions([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Changing the process invalidates whatever is on screen.
  const handleProcessChange = (label, option) => {
    setSelectedLabel(label);
    setSelectedProcessId(option ? option.value : null);
    setSelectedCompanyId(option ? option.companyId : null);
    setAnalysis(null);
    setStatus(STATUS.idle);
    setScenarioOpen(false);
  };

  const runAnalysis = async () => {
    if (!selectedProcessId) return;

    setStatus(STATUS.loading);

    try {
      const payload = await getWhatIfAnalysis(selectedProcessId);
      setAnalysis(normalizeWhatIfResponse(payload));
      setStatus(STATUS.ready);
      setScenarioOpen(false);
    } catch (error) {
      console.error("What-if analysis failed:", error);
      setFailure({
        title: FAILURE_TITLES[error?.code] || "Unable to analyse process",
        message: error?.message || "Something went wrong. Please try again.",
      });
      setAnalysis(null);
      setStatus(STATUS.error);
    }
  };

  const isLoading = status === STATUS.loading;

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">What-if Analysis</h2>
            <p className="page-subtitle">
              Cycle time, cost and RACI staffing for a process
            </p>
          </div>
        </div>
      </div>

      <div className="page-content whatif">
        <div className="role-card">
          <div className="section-title">Select Process</div>

          <div className="form-grid" style={{ marginBottom: 12 }}>
            <div className="form-group">
              <label htmlFor="whatif-time-unit">Time Unit</label>
              <select
                id="whatif-time-unit"
                className="form-input"
                value={timeUnit}
                onChange={(event) => setTimeUnit(event.target.value)}
              >
                {TIME_UNITS.map((unit) => (
                  <option key={unit.value} value={unit.value}>
                    {unit.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="whatif-currency">Currency</label>
              <select
                id="whatif-currency"
                className="form-input"
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
              >
                {CURRENCY_TYPES.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Process</label>
              <SearchableDropdown
                id="whatif-process-dropdown"
                className="input searchable-combobox"
                value={selectedLabel}
                options={processOptions}
                placeholder="Search and select a process..."
                clearOnFocus
                onChange={handleProcessChange}
              />
            </div>
            <div className="form-group" style={{ alignSelf: "end" }}>
              <button
                type="button"
                className="primary-btn"
                disabled={!selectedProcessId || isLoading}
                onClick={runAnalysis}
              >
                {isLoading ? "Analysing..." : "Analyse Process"}
              </button>
            </div>
          </div>

          {isLoading && (
            <div className="muted" style={{ marginTop: 8 }}>
              Fetching analysis...
            </div>
          )}
        </div>

        {status === STATUS.ready && analysis && (
          <>
            <MetricsPanel
              metrics={analysis.metrics}
              summary={analysis.summary}
              warnings={analysis.warnings}
              eliminatedCount={analysis.totals.eliminatedTaskIds.length}
              timeUnit={timeUnit}
              currency={currency}
              rates={rates}
            />
            <RaciMatrix
              tasks={analysis.tasks}
              totals={analysis.totals}
              timeUnit={timeUnit}
              currency={currency}
              rates={rates}
            />

            {scenarioOpen ? (
              <ScenarioEditor
                tasks={analysis.tasks}
                metrics={analysis.metrics}
                processId={selectedProcessId}
                companyId={selectedCompanyId}
                timeUnit={timeUnit}
                currency={currency}
                rates={rates}
                onClose={() => setScenarioOpen(false)}
              />
            ) : (
              <div className="role-card wif-scenario-cta">
                <div>
                  <div className="section-title">Create Scenario</div>
                  <p className="muted">
                    Reassign RACI jobs and time allocations to model a to-be
                    staffing plan against this analysis.
                  </p>
                </div>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={!analysis.tasks.length}
                  onClick={() => setScenarioOpen(true)}
                >
                  Create Scenario
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <FormModal
        open={Boolean(failure)}
        title={failure?.title || ""}
        size="compact"
        onCancel={() => setFailure(null)}
        footer={
          <button
            type="button"
            className="primary-btn"
            onClick={() => setFailure(null)}
          >
            OK
          </button>
        }
      >
        <p className="wif-failure-message">{failure?.message}</p>
      </FormModal>
    </div>
  );
};

export default WhatIfAnalysisPage;
